import React from 'react';
import i18next from 'i18next';
/**
 * The language i18next currently resolves in, re-read whenever it changes.
 *
 * The app renders as soon as i18next has initialised, in the language the saved locale or the
 * browser suggests, which can be one fetch before `setLocale` has finished switching to the user's
 * own language. Most screens re-render as their data arrives and pick the real language up on the
 * way, but a failed OIDC callback does not: it renders once from the query string and then never
 * again, so without this it explains the failure in the boot language instead of the user's.
 *
 * Pass the result as `lng` to `i18next.t` rather than only using it as a dependency. The React
 * Compiler caches a `t($ => ...)` lookup for the lifetime of the component instance, so a re-render
 * on its own would not re-resolve the copy.
 *
 * @returns The current i18next language, to pass as `lng` when resolving copy
 *
 * @example
 * ```typescript
 * const language = useI18nextLanguage();
 *
 * return <Text variant="body">{i18next.t($ => $.login.backToLogin, {lng: language})}</Text>;
 * ```
 */
export const useI18nextLanguage = () => {
    const [language, setLanguage] = React.useState(i18next.language);
    React.useEffect(() => {
        i18next.on('languageChanged', setLanguage);
        // The switch can land between the first render and this effect, which would leave the
        // subscription with nothing left to report.
        setLanguage(i18next.language);
        return () => {
            i18next.off('languageChanged', setLanguage);
        };
    }, []);
    return language;
};
